import React, { useState, useEffect } from 'react';
import { Card, Spin, Table, Input, Button, Select, Drawer, Space } from 'antd';
import {
  WarningOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import ReportService from '../../../services/ReportService';

const { Option } = Select;

const CRITICAL_LEVEL = 10; // Statik kritik seviye

const CriticalStockReports = () => {
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [draftSelectedBranch, setDraftSelectedBranch] = useState(null);

  const [criticalStock, setCriticalStock] = useState([]);
  const [criticalStockLoading, setCriticalStockLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const token = localStorage.getItem("token");
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const companyCode = userDetails?.CompanyCode || 'Techmax';

  // Şubeleri yükle
  useEffect(() => {
    const loadBranches = async () => {
      try {
        const response = await ReportService.company.getBranches({
          token,
          CompanyCode: companyCode
        });
        if (response.status === 200) {
          const data = Array.isArray(response.data) ? response.data : (response.data?.data || response.data || []);
          setBranches(data);
        }
      } catch (error) {
        console.error('Error loading branches:', error);
      }
    };
    loadBranches();
  }, [token, companyCode]);

  const fetchCriticalStock = async (branchCodeOverride = null) => {
    try {
      setCriticalStockLoading(true);
      const response = await ReportService.stock.getBranchCriticalStockReport({
        token,
        CompanyCode: companyCode,
        BranchCode: branchCodeOverride !== null && branchCodeOverride !== undefined ? branchCodeOverride : (selectedBranch || undefined),
        CriticalLevel: CRITICAL_LEVEL
      });

      if (response.status === 200) {
        const data = Array.isArray(response.data) ? response.data : (response.data?.data || response.data || []);
        setCriticalStock(data);
      } else {
        setCriticalStock([]);
      }
    } catch (error) {
      console.error('Error fetching critical stock:', error);
      setCriticalStock([]);
    } finally {
      setCriticalStockLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchCriticalStock();
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleBranchChange = (value) => {
    setSelectedBranch(value);
    fetchCriticalStock(value);
  };

  const openFilters = () => {
    setDraftSelectedBranch(selectedBranch);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setSelectedBranch(draftSelectedBranch);
    setIsFilterOpen(false);
    fetchCriticalStock(draftSelectedBranch || null);
  };

  const clearFilters = () => {
    const defaultSelectedBranch = null;
    setDraftSelectedBranch(defaultSelectedBranch);
    setSelectedBranch(defaultSelectedBranch);
    fetchCriticalStock(null);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const criticalStockColumns = [
    {
      title: <span className="text-base">Şube Adı</span>,
      dataIndex: 'SubeAdi',
      key: 'SubeAdi',
      width: isMobile ? '25%' : '20%',
      render: (text) => <span className="text-base font-semibold">{text}</span>,
      ellipsis: isMobile
    },
    {
      title: <span className="text-base">Stok Kodu</span>,
      dataIndex: 'StokKodu',
      key: 'StokKodu',
      width: isMobile ? '20%' : '15%',
      render: (text) => <span className="text-base">{text}</span>,
      ellipsis: isMobile
    },
    {
      title: <span className="text-base">Stok Adı</span>,
      dataIndex: 'StokAdi',
      key: 'StokAdi',
      width: isMobile ? '25%' : '20%',
      render: (text) => <span className="text-base">{text}</span>,
      ellipsis: isMobile
    },
    {
      title: <span className="text-base">Stok Tipi</span>,
      dataIndex: 'StokTipi',
      key: 'StokTipi',
      width: isMobile ? '15%' : '12%',
      render: (text) => <span className="text-base">{text}</span>,
      responsive: ['md'],
    },
    {
      title: <span className="text-base">Mevcut Bakiye</span>,
      dataIndex: 'MevcutBakiye',
      key: 'MevcutBakiye',
      width: isMobile ? '15%' : '12%',
      render: (value, record) => {
        const bakiyeText = `${formatNumber(value || 0)} ${record.OlcuBirimi || ''}`;
        const ambalajText = record.AmbalajBirimi && record.DonusumMiktari
          ? ` (${formatNumber((value || 0) / (record.DonusumMiktari || 1))} ${record.AmbalajBirimi})`
          : '';
        return (
          <span className="text-base font-semibold text-red-600">
            {bakiyeText}{ambalajText}
          </span>
        );
      },
      sorter: (a, b) => (a.MevcutBakiye || 0) - (b.MevcutBakiye || 0),
    },
    {
      title: <span className="text-base">Kritik Seviye</span>,
      dataIndex: 'KritikSeviye',
      key: 'KritikSeviye',
      width: isMobile ? '15%' : '12%',
      render: (value, record) => (
        <span className="text-base font-semibold text-orange-600">
          {formatNumber(value || 0)} {record.OlcuBirimi || ''}
        </span>
      ),
      responsive: ['md'],
    },
  ];

  const filterData = (data) => {
    if (!searchText) return data;
    const needle = searchText.toLowerCase();
    return data.filter(row =>
      String(row.SubeAdi || '').toLowerCase().includes(needle) ||
      String(row.StokKodu || '').toLowerCase().includes(needle) ||
      String(row.StokAdi || '').toLowerCase().includes(needle) ||
      String(row.StokTipi || '').toLowerCase().includes(needle)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-50 via-white to-slate-50 border border-slate-200/60 shadow-lg backdrop-blur-sm mb-4">
        <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 via-orange-500/5 to-yellow-500/5"></div>
        <div className="relative px-3 py-2 sm:px-4 sm:py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg sm:rounded-xl shadow-lg">
              <WarningOutlined className="text-white text-lg sm:text-xl" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent leading-tight">
                Kritik Stok
              </h1>
              <p className="text-slate-500 text-xs sm:text-sm font-medium">
                Ham madde ve yarı mamüller için kritik stok seviyesi raporu (Kritik Seviye: {CRITICAL_LEVEL} birim)
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchCriticalStock(selectedBranch || null)}
              loading={criticalStockLoading}
            >
              Yenile
            </Button>
            <Button icon={<FilterOutlined />} onClick={openFilters}>Filtreler</Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <Input
          allowClear
          placeholder="Şube, stok kodu veya stok adı ara..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="large"
          className="flex-1 rounded-lg"
        />
      </div>

      {/* Critical Stock Report */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <WarningOutlined className="text-red-500" />
            <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>
              Kritik Stok Raporu
            </h2>
          </div>
        }
        bordered={false}
        className="shadow-lg rounded-2xl border border-gray-100 bg-white"
        styles={{ body: { padding: isMobile ? 12 : 18 } }}
      >
        {criticalStock.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 text-base">Kritik seviyenin altında stok bulunmamaktadır.</p>
          </div>
        ) : (
          <Table
            columns={criticalStockColumns}
            dataSource={filterData(criticalStock)}
            loading={criticalStockLoading}
            rowKey={(record) => `${record.SubeKodu}-${record.StokKodu}`}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Toplam ${total} kritik stok`,
            }}
            size={isMobile ? 'small' : 'middle'}
          />
        )}
      </Card>

      <Drawer title="Filtreler" placement="right" open={isFilterOpen} onClose={() => setIsFilterOpen(false)} width={isMobile ? 280 : 360}>
        <Space direction="vertical" size="middle" className="w-full">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Şube</span>
            <Select
              value={draftSelectedBranch}
              onChange={(value) => setDraftSelectedBranch(value)}
              placeholder="Şube seçiniz"
              className="w-full"
              allowClear
            >
              {branches.map((branch) => (
                <Select.Option key={branch.SubeKodu} value={branch.SubeKodu}>
                  {branch.SubeAdi}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="flex items-center justify-between gap-2 pt-2">
            <Button onClick={clearFilters}>Temizle</Button>
            <Button type="primary" danger ghost={false} onClick={applyFilters} className="!bg-rose-600 !border-rose-600 !text-white hover:!bg-rose-700 hover:!border-rose-700">Uygula</Button>
          </div>
        </Space>
      </Drawer>
    </div>
  );
};

export default CriticalStockReports;

